import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router";
import { useAuth, USER_ROLE } from "contexts/auth/auth.context";
import { DEFAULT_CALENDAR_CONFIG, useCalendarConfig } from "contexts/calendar/calendar.context";
import { useSearchParams, useLocation } from "react-router-dom";
import login from "api/shared/use-login";

const schema = Yup.object().shape({
    login: Yup.string().required("Login jest wymagany"),
    password: Yup.string().required("Hasło jest wymagane"),
});

export type FormValues = Yup.InferType<typeof schema>;

export const useNativeLogin = () => {
    const { loginContext } = useAuth();
    const { updateCalendarSettings } = useCalendarConfig();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams] = useSearchParams();
    const appointmentId = searchParams.get("appointmentId");
    const chat = searchParams.get("tab") === "chat";
    const documentation = searchParams.get("tab") === "documentation";

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        control,
        watch,
        setValue
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    // przenoszenie na odpowiednie ekrany po logowaniu poprzez link sms
    const handleNavigate = (role: USER_ROLE) => {
        const routes = {
            basic: {
                [USER_ROLE.ROLE_DOCTOR]: "/",
                [USER_ROLE.ROLE_PATIENT]: "/clientAppointments",
            },
            appointmentChat: {
                [USER_ROLE.ROLE_DOCTOR]: `/appointmentDetails/${appointmentId}?tab=chat`,
                [USER_ROLE.ROLE_PATIENT]: `/clientAppointmentDetails/${appointmentId}?tab=chat`,
            },
            appointmentDocumentation: {
                [USER_ROLE.ROLE_DOCTOR]: `/appointmentDetails/${appointmentId}?tab=documentation`,
                [USER_ROLE.ROLE_PATIENT]: `/clientAppointmentDetails/${appointmentId}?tab=documentation`,
            },
            appointmentDetails: {
                [USER_ROLE.ROLE_DOCTOR]: `/appointmentDetails/${appointmentId}?tab=details`,
                [USER_ROLE.ROLE_PATIENT]: `/clientAppointmentDetails/${appointmentId}?tab=details`,
            },
        };

        let targetRoute;

        const redirectTo = location.state?.redirectTo;

        if (appointmentId) {
            if (chat) targetRoute = routes.appointmentChat[role];
            else if (documentation) targetRoute = routes.appointmentDocumentation[role];
            else targetRoute = routes.appointmentDetails[role];
        } else if (redirectTo && role === USER_ROLE.ROLE_PATIENT) {
            targetRoute = redirectTo;
        } else {
            targetRoute = routes.basic[role];
        }

        if (targetRoute) navigate(targetRoute);
    };

    const { mutate, isPending, error } = useMutation({
        mutationFn: (payload: FormValues) => login(payload),
        onSuccess: (result) => {

            loginContext(result);

            // update calendar settings
            const calendar = result.calendar;
            updateCalendarSettings({
                config: calendar ? {
                    start: calendar.start ? calendar.start.slice(0, 5) : DEFAULT_CALENDAR_CONFIG.start,
                    end: calendar.end ? calendar.end.slice(0, 5) : DEFAULT_CALENDAR_CONFIG.end,
                    step: calendar.step != null ? Number(calendar.step) : DEFAULT_CALENDAR_CONFIG.step,
                    showWeekends: calendar.showWeekends ?? DEFAULT_CALENDAR_CONFIG.showWeekends,
                    isAllowedTimeLock: calendar.allowTimeLock ?? DEFAULT_CALENDAR_CONFIG.isAllowedTimeLock,
                    backgroundColorScheme: calendar.backgroundColorScheme ?? 'Białe',
                } : DEFAULT_CALENDAR_CONFIG
            });

            handleNavigate(result.role);
        },
        onError: (err: any) => {
            let message = "Wystąpił niespodziewany błąd.";

            if (isAxiosError(err)) {
                message = err.response?.data?.message || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }

            console.log(`Login error: ${message}`);
        }
    });

    const onSubmit = handleSubmit((formData) => {
        mutate(formData);
    });

    return {
        onSubmit,
        register,
        loading: isPending,
        loginError: (error as any)?.response?.data?.message || (error as any)?.message,
        formErrors: errors,
        control,
        watch,
        setValue,
        isValid
    };
};
